/**
 * /me/assets — an employee's own current holdings + history (ADR-0024 Phase 4).
 * Employee-layout; resolves from the token server-side (never a URL id).
 */
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { meLayoutRoute } from './meLayout.js';
import { listMyAssets, type Holding } from '../api/assets.js';
import { fmtAssignedAt, fmtDate } from './assetShared.js';
import { Alert } from '../components/Alert.js';

export const myAssetsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/assets',
  component: MyAssetsPage,
});

function MyAssetsPage(): JSX.Element {
  const query = useQuery({ queryKey: ['me', 'assets'], queryFn: listMyAssets });
  const assets = query.data?.assets ?? [];
  const current = assets.filter((a) => a.current);
  const history = assets.filter((a) => !a.current);

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <header className="space-y-1">
        <h1 className="font-serif text-3xl">My assets</h1>
        <p className="text-sm text-gray-400">Company assets currently in your custody, and your history.</p>
      </header>

      {query.isLoading ? <p className="text-sm text-gray-400">Loading…</p> : null}
      {query.isError ? <Alert variant="error">Could not load your assets.</Alert> : null}

      <div className="space-y-3">
        <h2 className="text-sm uppercase tracking-wide text-gray-500">Currently held</h2>
        {current.length === 0 ? (
          <p className="text-sm text-gray-400">You currently hold no company assets.</p>
        ) : (
          <ul className="space-y-2">
            {current.map((a) => (
              <HoldingRow key={a.id} a={a} />
            ))}
          </ul>
        )}
      </div>

      {history.length > 0 ? (
        <div className="space-y-3">
          <h2 className="text-sm uppercase tracking-wide text-gray-500">History</h2>
          <ul className="space-y-2 opacity-70">
            {history.map((a) => (
              <HoldingRow key={a.id} a={a} />
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function HoldingRow({ a }: { a: Holding }): JSX.Element {
  return (
    <li className="rounded border border-gray-900 px-4 py-3 flex items-center justify-between text-sm">
      <div>
        <span className="font-mono text-gray-200">{a.assetTag ?? '—'}</span>
        <span className="text-gray-400"> · {a.name ?? '—'}</span>
      </div>
      <div className="text-right text-xs text-gray-400">
        <div>Received: {fmtAssignedAt(a.assignedAt)}</div>
        {a.returnedAt ? <div>Returned: {fmtDate(a.returnedAt)}</div> : null}
      </div>
    </li>
  );
}
